import type { FlashcardElementInput } from '../../flashcard/elements/registry'
import type { PrimaryCheckboxRef } from './primaryCheckbox'

const normalizeBooleanArray = (value: unknown): boolean[] =>
  Array.isArray(value) ? value.map(Boolean) : []

const normalizeCount = (value: unknown, fallback: number) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return fallback
  return Math.max(0, Math.floor(value))
}

const normalizeValuesForElement = (element: FlashcardElementInput, values: boolean[]) => {
  if (element.type !== 'checkbox-group') return values
  const record = element as Record<string, unknown>
  const count = normalizeCount(record.count, values.length)
  const total = Math.max(values.length, count)
  return Array.from({ length: total }, (_, idx) => Boolean(values[idx]))
}

const readonlyKey = (base: string) =>
  base.startsWith('pfc:readonly:') ? base : `pfc:readonly:${base}`

export function applyDeckPlayerCheckboxState(opts: {
  front: FlashcardElementInput[]
  back: FlashcardElementInput[]
  primary: PrimaryCheckboxRef
  primaryValues: boolean[]
  /** When true (e.g. flowchart mode), the primary checkbox also becomes read-only. */
  primaryReadOnly?: boolean
}): { front: FlashcardElementInput[]; back: FlashcardElementInput[] } {
  const primary = opts.primary

  // In the player, the primary checkbox group must stay clickable even if other
  // elements (text/images) overlap it. We achieve this by boosting its layer,
  // which maps to runtime z-index.
  const PRIMARY_LAYER_BOOST = 10_000

  const patchFace = (face: 'front' | 'back', elements: FlashcardElementInput[]) => {
    return (Array.isArray(elements) ? elements : []).map((el, index) => {
      if (!el || typeof el !== 'object') return el
      if (el.type !== 'checkbox-group') return el

      const record = el as Record<string, unknown>
      const existingKey = typeof record.stateKey === 'string' ? record.stateKey : ''
      const fallbackKey = `${face}-${index}`
      const baseKey = existingKey || fallbackKey

      const isPrimary = Boolean(primary && primary.face === face && primary.elementIndex === index)

      if (isPrimary) {
        const existingLayer = typeof record.layer === 'number' ? record.layer : 0
        if (opts.primaryReadOnly) {
          // Flowchart mode: update displayed values but block interaction.
          return {
            ...(el as Record<string, unknown>),
            values: normalizeValuesForElement(el, normalizeBooleanArray(opts.primaryValues)),
            layer: PRIMARY_LAYER_BOOST + existingLayer,
            stateKey: readonlyKey(existingKey || `pfc:primary:${baseKey}`),
          } as FlashcardElementInput
        }
        return {
          ...(el as Record<string, unknown>),
          values: normalizeValuesForElement(el, normalizeBooleanArray(opts.primaryValues)),
          layer: PRIMARY_LAYER_BOOST + existingLayer,
          // Keep stateKey stable if already present.
          ...(existingKey ? {} : { stateKey: `pfc:primary:${baseKey}` }),
        } as FlashcardElementInput
      }

      // Non-primary checkbox groups: keep them rendered but make them read-only.
      return {
        ...(el as Record<string, unknown>),
        stateKey: readonlyKey(baseKey),
      } as FlashcardElementInput
    })
  }

  return {
    front: patchFace('front', opts.front),
    back: patchFace('back', opts.back),
  }
}

export const isDone = (values: boolean[]) =>
  Array.isArray(values) && values.length > 0 && values.every(Boolean)
